import { apiClient } from './api';
import { API_CONFIG } from '@/config/api.config';
import type {
  LoginCredentials,
  AcceptInviteCredentials,
  AuthData,
  BackendAuthResponse,
  BackendMeResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  BackendMessageResponse,
} from '@/types/auth';

/**
 * Transform backend response to AuthData format
 */
function toAuthData(response: BackendAuthResponse): AuthData {
  return {
    token: response.tokens.accessToken,
    refreshToken: response.tokens.refreshToken ?? null,
    user: response.user,
    organization: response.organization ?? null,
  };
}

export const authService = {
  /**
   * Login with email and password
   * Public endpoint - no auth required
   */
  login: async (credentials: LoginCredentials): Promise<AuthData> => {
    const response = await apiClient.post<BackendAuthResponse>(
      API_CONFIG.ENDPOINTS.AUTH.LOGIN,
      credentials,
      { skipAuth: true },
    );

    return toAuthData(response);
  },

  /**
   * Accept an organization invitation — sets the user's permanent password in Keycloak.
   * Public endpoint: no auth token required.
   * On success the caller should trigger keycloak.login() (no in-app login page).
   */
  acceptInvite: async (credentials: AcceptInviteCredentials): Promise<void> => {
    await apiClient.post(
      API_CONFIG.ENDPOINTS.AUTH.ACCEPT_INVITE,
      credentials,
      { skipAuth: true },
    );
  },

  /**
   * Probe the status of an invitation token before showing the form.
   * Public endpoint, returns one of: valid | used | expired | not_found.
   */
  getInviteStatus: async (
    token: string,
  ): Promise<{
    status: 'valid' | 'used' | 'expired' | 'not_found';
    email?: string;
    role?: string;
    organizationName?: string;
  }> => {
    return apiClient.get<{
      status: 'valid' | 'used' | 'expired' | 'not_found';
      email?: string;
      role?: string;
      organizationName?: string;
    }>(
      `${API_CONFIG.ENDPOINTS.AUTH.INVITE_STATUS}?token=${encodeURIComponent(token)}`,
      { skipAuth: true },
    );
  },

  /**
   * Get current authenticated user
   */
  me: async (): Promise<BackendMeResponse> => {
    return apiClient.get<BackendMeResponse>(API_CONFIG.ENDPOINTS.AUTH.ME);
  },

  /**
   * Request password reset email
   * Public endpoint - no auth required
   */
  forgotPassword: async (request: ForgotPasswordRequest): Promise<string> => {
    const response = await apiClient.post<BackendMessageResponse>(
      API_CONFIG.ENDPOINTS.AUTH.FORGOT_PASSWORD,
      request,
      { skipAuth: true },
    );

    return response.message;
  },

  /**
   * Reset password using token from email
   * Public endpoint - no auth required
   */
  resetPassword: async (request: ResetPasswordRequest): Promise<string> => {
    const response = await apiClient.post<BackendMessageResponse>(
      API_CONFIG.ENDPOINTS.AUTH.RESET_PASSWORD,
      request,
      { skipAuth: true },
    );

    return response.message;
  },
};
